import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, HourglassMedium, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        // keep polling
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setState("timeout");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F4F5F7] grid place-items-center px-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-sm w-full text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        {state === "paid" && (
          <div data-testid="payment-success-view">
            <CheckCircle size={56} weight="duotone" className="text-status-green mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold mt-4">Payment received!</h1>
            <p className="text-sm text-slate-500 mt-2">The fee payment was recorded and the school has been updated.</p>
          </div>
        )}
        {state === "checking" && (
          <div data-testid="payment-checking-view">
            <HourglassMedium size={56} weight="duotone" className="text-brand-yellow mx-auto animate-pulse" />
            <h1 className="font-heading text-2xl font-extrabold mt-4">Confirming payment…</h1>
            <p className="text-sm text-slate-500 mt-2">Hold on, we're verifying with the payment gateway.</p>
          </div>
        )}
        {state === "timeout" && (
          <div data-testid="payment-timeout-view">
            <HourglassMedium size={56} weight="duotone" className="text-status-amber mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold mt-4">Still processing</h1>
            <p className="text-sm text-slate-500 mt-2">Your payment is being processed. It will reflect in the ledger shortly.</p>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="payment-failed-view">
            <XCircle size={56} weight="duotone" className="text-status-red mx-auto" />
            <h1 className="font-heading text-2xl font-extrabold mt-4">Something went wrong</h1>
            <p className="text-sm text-slate-500 mt-2">We couldn't verify this payment session.</p>
          </div>
        )}
        <Link to="/parent" className="block mt-8">
          <Button data-testid="payment-back-btn" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
            Back to my feed
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#F4F5F7] grid place-items-center px-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-sm w-full text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="payment-cancel-view">
        <XCircle size={56} weight="duotone" className="text-slate-300 mx-auto" />
        <h1 className="font-heading text-2xl font-extrabold mt-4">Payment cancelled</h1>
        <p className="text-sm text-slate-500 mt-2">No money was charged. You can try again anytime from the Fees tab.</p>
        <Link to="/parent" className="block mt-8">
          <Button data-testid="payment-cancel-back-btn" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
            Back to my feed
          </Button>
        </Link>
      </div>
    </div>
  );
}
